import { useState, useEffect } from "react";
import { useLocation } from "react-router-dom";
import { useAppContextNonNull } from "@contexts/AppContext";
import { buildRestUrl, buildAuthHeadersForFetch } from "../services/RestService";
import RestEndpointsEnum from "../models/RestEndpointsEnum";
import IDailyRouteGroupData from "../models/IDailyRouteGroupData";

export function useDailyRouteGroups() {
    const { userModel, supplierModel } = useAppContextNonNull();
    const location = useLocation();

    const params = new URLSearchParams(location.search);
    const dailyRoutesParam = params.get("dailyRoutes") || "";
    const hasDailyRoutes = dailyRoutesParam.length > 0;

    const [dailyRouteGroups, setDailyRouteGroups] = useState<IDailyRouteGroupData[]>([]);
    const [allOrderGuids, setAllOrderGuids] = useState<string[]>([]);
    const [loading, setLoading] = useState(hasDailyRoutes);

    useEffect(() => {
        if (!hasDailyRoutes) return;

        const guids = dailyRoutesParam.split(",").filter(Boolean);

        const fetchDailyRoutes = async () => {
            setLoading(true);

            try {
                const results = await Promise.all(
                    guids.map(async (guid) => {
                        const url = buildRestUrl({
                            endpoint: RestEndpointsEnum.DailyRoute,
                            params: { dailyRouteGuid: guid },
                        });

                        const headers = buildAuthHeadersForFetch({ userModel, supplierModel });
                        const response = await fetch(url, { method: "GET", headers });

                        if (!response.ok) {
                            throw new Error(`Failed to fetch daily route: ${response.statusText}`);
                        }

                        const data = await response.json();
                        return data.dailyRoute || data;
                    })
                );

                const groups: IDailyRouteGroupData[] = results.map((dailyRoute) => {
                    const orderGuids = (dailyRoute.Daily_Route_Order_Maps || []).map((map: any) => map.Order?.guid).filter(Boolean);

                    return { dailyRoute, orderGuids };
                });

                const allGuids = groups.flatMap((g) => g.orderGuids);

                setDailyRouteGroups(groups);
                setAllOrderGuids(allGuids);
            } catch (err) {
                console.error("Error fetching daily route groups:", err);
            } finally {
                setLoading(false);
            }
        };

        fetchDailyRoutes();
    }, [dailyRoutesParam]);

    return { dailyRouteGroups, allOrderGuids, loading, hasDailyRoutes };
}
